/**
 * The plumbing every action group shares: read state, write an immutable patch, register undo
 * entries, raise toasts. Actions never touch persistence, the DOM, three.js or the network —
 * wave-3/4 code calls those and then dispatches an action with the result.
 */

import { evaluateHipsY, evaluatePose, identityQuat, quatEquals } from '../../shared';
import type {
  CanonicalAnimationClip,
  CanonicalBoneId,
  KeyframeSelection,
  Pose,
} from '../../shared';
import type { ClipEventRef, EditorState } from '../types';
import { MAX_UI_ERRORS } from '../types';
import type { UndoStack } from '../undo';

export interface ActionContext {
  getState(): EditorState;
  /** Shallow-merge a patch into the state. Unchanged keys are ignored (no notification). */
  set(patch: Partial<EditorState>): void;
  history: UndoStack;
  /** Monotonic toast id generator. */
  nextToastId(): number;
}

/** The pose the clip evaluates to at `frame` (defaults to the playhead). Empty without a clip. */
export function evaluatedPose(state: EditorState, frame?: number): Pose {
  if (!state.clip) return {};
  return evaluatePose(state.clip, frame ?? state.playhead.frame);
}

/** The hips offset the clip evaluates to at `frame` (defaults to the playhead). 0 without a clip. */
export function evaluatedHipsY(state: EditorState, frame?: number): number {
  if (!state.clip) return 0;
  return evaluateHipsY(state.clip, frame ?? state.playhead.frame);
}

/** The live slices a clip evaluation produces — `livePose` and its scalar companion. */
export interface LiveSnapshot {
  livePose: Pose;
  liveHipsY: number;
}

/**
 * Both live slices at one frame, in one call.
 *
 * Every writer that re-derives `livePose` from the clip must re-derive `liveHipsY` at the SAME
 * frame, or playback and scrubbing would move the rotations while the pelvis stayed where the
 * last edit left it. Handing back one object is what makes "did I remember the hips here?"
 * answerable by reading the call rather than by auditing six functions.
 */
export function evaluatedLive(
  clip: CanonicalAnimationClip | null,
  frame: number,
): LiveSnapshot {
  if (!clip) return { livePose: {}, liveHipsY: 0 };
  return { livePose: evaluatePose(clip, frame), liveHipsY: evaluateHipsY(clip, frame) };
}

/** Tolerance for "this bone is posed away from what the clip evaluates to". */
export const POSE_EPSILON = 1e-6;

/**
 * The `livePose` to show after a clip edit that moved keys under a STATIONARY playhead
 * (`reverseSelectedKeys`, `scaleSelectedKeys`): the frame did not change, so nothing else would
 * refresh the viewport, yet the rotation at that frame did.
 *
 * A blanket `evaluatePose(next, frame)` is wrong — it silently destroys un-keyed work. So each
 * bone is classified against the pose the OLD clip produced at the same frame:
 *  - still equal to it → the bone was only mirroring the clip, so it follows the clip to the
 *    new value;
 *  - different → un-keyed FK/IK work the user has not committed to a keyframe, kept verbatim.
 *
 * That keeps the module invariant ("clip edits leave `livePose` alone") true where it matters:
 * `setPlayhead` stays the ONLY path that may discard un-keyed posing, and it offers an undo
 * entry plus a toast when it does. Bones absent from a clip evaluate to identity, matching
 * `hasUnkeyedPose` in `clip.ts`.
 */
export function reposeForClipEdit(
  previous: CanonicalAnimationClip,
  next: CanonicalAnimationClip,
  livePose: Pose,
  frame: number,
): Pose {
  const before = evaluatePose(previous, frame);
  const after = evaluatePose(next, frame);
  const out: Pose = { ...after };

  for (const bone of Object.keys(livePose) as CanonicalBoneId[]) {
    const live = livePose[bone];
    if (!live) continue;
    if (!quatEquals(live, before[bone] ?? identityQuat(), POSE_EPSILON)) out[bone] = live;
  }
  return out;
}

/**
 * `reposeForClipEdit` for the hips offset — the same classification, one scalar instead of a map:
 * a live value still equal to what the OLD clip produced at this frame was only mirroring the
 * clip and follows it to the new value; a different one is un-keyed work and is kept verbatim.
 *
 * Kept as its own function rather than folded into the pose one because the two are used
 * together in exactly one shape (`reposeLiveForClipEdit`) and separately never — but a caller
 * that has only the scalar (a future hips-only edit) must not have to build a `Pose` to ask.
 */
export function reposeHipsYForClipEdit(
  previous: CanonicalAnimationClip,
  next: CanonicalAnimationClip,
  liveHipsY: number,
  frame: number,
): number {
  const before = evaluateHipsY(previous, frame);
  if (Math.abs(liveHipsY - before) > POSE_EPSILON) return liveHipsY;
  return evaluateHipsY(next, frame);
}

/** Both live slices re-classified for a clip edit under a stationary playhead. */
export function reposeLiveForClipEdit(
  previous: CanonicalAnimationClip,
  next: CanonicalAnimationClip,
  live: LiveSnapshot,
  frame: number,
): LiveSnapshot {
  return {
    livePose: reposeForClipEdit(previous, next, live.livePose, frame),
    liveHipsY: reposeHipsYForClipEdit(previous, next, live.liveHipsY, frame),
  };
}

export function showToast(ctx: ActionContext, kind: 'info' | 'error', message: string): void {
  const ui = ctx.getState().ui;
  ctx.set({ ui: { ...ui, toast: { kind, message, id: ctx.nextToastId() } } });
}

/** Toast + append to the error log in one step (used by the AppError catch paths). */
export function reportError(ctx: ActionContext, message: string): void {
  const ui = ctx.getState().ui;
  const errors = [...ui.errors, message];
  if (errors.length > MAX_UI_ERRORS) errors.splice(0, errors.length - MAX_UI_ERRORS);
  ctx.set({ ui: { ...ui, errors, toast: { kind: 'error', message, id: ctx.nextToastId() } } });
}

/**
 * Apply an edited clip and register ONE undo entry holding just the clip slice
 * (clip + dirty flag + key selection). Returns false when the edit was a no-op.
 *
 * Clip edits deliberately leave `livePose` alone — un-keyed posing survives keyframe edits and
 * is only recomputed when the playhead moves or `resetLivePose()` runs.
 *
 * `nextLivePose` is the exception, for edits that move keys under a stationary playhead: pass
 * the pose from `reposeForClipEdit` and it becomes part of the SAME undo entry. It has to be,
 * or undo would restore the clip while the viewport kept showing the edited pose — and the next
 * `keyBone()` would then write that stale rotation into the restored clip. Omit it and
 * `livePose` is untouched, exactly as before.
 *
 * `nextFrame` is the same exception for the playhead, for edits that RETIME the clip (`setFps`):
 * the frame number means a different instant before and after, so the edit has to move the
 * playhead with it. Before this parameter existed `setFps` wrote the frame with a bare
 * `ctx.set(...)` after the commit, i.e. outside the undo entry, and undo restored the 30 fps clip
 * while leaving the playhead on the 60 fps frame — the audit reproduced the rest of that chain
 * live (§2.2 #2): `livePose` stayed on the retimed value too, and the next `K` wrote it over the
 * user's original keyframe, with no toast and no error. Anything derived from the clip must ride
 * the clip's own undo entry.
 *
 * `nextLiveHipsY` is `nextLivePose`'s scalar companion and rides the same entry for the same
 * reason: the hips offset is derived from the clip exactly like a rotation is, so an edit that
 * re-derives one and not the other leaves the pelvis at a height the restored clip does not
 * produce.
 *
 * Only the `frame` FIELD is restored, never a captured `playhead` object: `speed`, `playing` and
 * `loop` are workspace state that keeps changing after the commit, so undo must spread whatever
 * the playhead holds at undo TIME (both closures re-read `ctx.getState()`). Capturing the object
 * would make "change fps, change playback speed, Ctrl+Z" silently revert the speed as well. Same
 * reasoning, same shape as `restore`/`reapply` in `setPlayhead`'s "Discard unkeyed pose" entry.
 */
export interface CommitClipEditOptions {
  nextSelection?: KeyframeSelection;
  coalesceKey?: string;
  nextLivePose?: Pose;
  /** Only meaningful together with `nextLivePose`; the two are one derived pair. */
  nextLiveHipsY?: number;
  nextFrame?: number;
  /**
   * Which event marker should be selected after this edit. Passed explicitly because an event is
   * referenced by `(frame, name)` and an edit can MOVE it: an action that drags a marker has to
   * hand over the ref it will have afterwards, or the next drag would address the old position.
   * Undo restores whatever was selected before.
   */
  nextEventSelection?: ClipEventRef | null;
}

/*
 * The 4th argument takes EITHER the options object above or the original positional selection.
 * The positional form is not a legacy leftover to be tidied away at leisure: `actions/events.ts`
 * still uses it (five calls), and a union here is what let the hips channel add a parameter
 * without a nine-argument call in the middle of an unrelated file. New call sites pass options.
 */
export function commitClipEdit(
  ctx: ActionContext,
  label: string,
  next: CanonicalAnimationClip,
  optionsOrSelection?: CommitClipEditOptions | KeyframeSelection,
  coalesceKey?: string,
  nextLivePose?: Pose,
  nextFrame?: number,
  nextEventSelection?: ClipEventRef | null,
): boolean {
  const options: CommitClipEditOptions =
    optionsOrSelection === undefined || Array.isArray(optionsOrSelection)
      ? {
          nextSelection: optionsOrSelection,
          coalesceKey,
          nextLivePose,
          nextFrame,
          nextEventSelection,
        }
      : optionsOrSelection;

  const state = ctx.getState();
  const prevClip = state.clip;
  const prevDirty = state.clipDirty;
  const prevSelection = state.keySelection;
  const prevLivePose = state.livePose;
  const prevLiveHipsY = state.liveHipsY;
  const prevFrame = state.playhead.frame;
  const prevEventSelection = state.selectedEvent;
  const selection = options.nextSelection ?? prevSelection;
  const nextLive = options.nextLivePose;
  const nextHipsY = options.nextLiveHipsY;
  const frame = options.nextFrame;
  const eventSelection = options.nextEventSelection;

  // A retime that lands on the same clip object but a different frame is NOT a no-op: dropping it
  // here would leave the caller's frame change unregistered and un-undoable.
  const framesEqual = frame === undefined || frame === prevFrame;
  if (prevClip === next && selection === prevSelection && framesEqual) return false;

  const apply = (): void => {
    ctx.set({
      clip: next,
      clipDirty: true,
      keySelection: selection,
      ...(nextLive ? { livePose: nextLive } : {}),
      ...(nextHipsY !== undefined ? { liveHipsY: nextHipsY } : {}),
      ...(frame !== undefined ? { playhead: { ...ctx.getState().playhead, frame } } : {}),
      ...(eventSelection !== undefined ? { selectedEvent: eventSelection } : {}),
    });
  };
  const revert = (): void => {
    ctx.set({
      clip: prevClip,
      clipDirty: prevDirty,
      keySelection: prevSelection,
      ...(nextLive ? { livePose: prevLivePose } : {}),
      ...(nextHipsY !== undefined ? { liveHipsY: prevLiveHipsY } : {}),
      ...(frame !== undefined ? { playhead: { ...ctx.getState().playhead, frame: prevFrame } } : {}),
      ...(eventSelection !== undefined ? { selectedEvent: prevEventSelection } : {}),
    });
  };

  apply();
  ctx.history.push({ label, key: options.coalesceKey, undo: revert, redo: apply });
  return true;
}

export function clampFrame(frame: number, durationFrames: number): number {
  if (!Number.isFinite(frame)) return 0;
  if (durationFrames <= 0) return 0;
  return Math.min(durationFrames, Math.max(0, frame));
}

export function keySelectionKey(bone: string, frame: number): string {
  return `${bone}:${frame}`;
}

/** Dedupe a keyframe selection, preserving first-seen order. */
export function dedupeSelection(selection: KeyframeSelection): KeyframeSelection {
  const seen = new Set<string>();
  const out: KeyframeSelection = [];
  for (const ref of selection) {
    const id = keySelectionKey(ref.bone, ref.frame);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ bone: ref.bone, frame: ref.frame });
  }
  return out;
}
